import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import type { OwnerWorkspaceRow } from '../../../services/ownerConsoleService'
import './WorkspaceSelector.css'

export type WorkspaceSelectorProps = {
  workspaces: OwnerWorkspaceRow[]
  value: string | null
  onChange: (id: string | null) => void
  placeholder?: string
  autoFocus?: boolean
  variant?: 'sticky' | 'inline'
  disabled?: boolean
}

const RECENT_KEY = 'owner_console.recent_workspaces'
const PINNED_KEY = 'owner_console.pinned_workspaces'

const safeReadIds = (key: string): string[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '[]') as unknown
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : []
  } catch {
    return []
  }
}

const safeWriteIds = (key: string, ids: string[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // Owner-only convenience state; storage failures should not block actions.
  }
}

const rankWorkspace = (row: OwnerWorkspaceRow, query: string) => {
  const needle = query.trim().toLowerCase()
  if (!needle) return 1
  const name = row.name.toLowerCase()
  const id = row.id.toLowerCase()
  if (name === needle) return 100
  if (name.startsWith(needle)) return 90
  if (name.includes(needle)) return 80
  if (id === needle) return 70
  if (id.startsWith(needle)) return 60
  if (id.includes(needle)) return 50
  let cursor = 0
  for (const char of needle) {
    cursor = name.indexOf(char, cursor)
    if (cursor === -1) return 0
    cursor += 1
  }
  return 30
}

const rowById = (rows: OwnerWorkspaceRow[], id: string | null) =>
  id ? rows.find((row) => row.id === id) ?? null : null

export const WorkspaceSelector = ({
  workspaces,
  value,
  onChange,
  placeholder = 'Select workspace',
  autoFocus = false,
  variant = 'inline',
  disabled = false,
}: WorkspaceSelectorProps) => {
  const [open, setOpen] = useState(Boolean(autoFocus))
  const [prevAutoFocus, setPrevAutoFocus] = useState(autoFocus)
  if (prevAutoFocus !== autoFocus) {
    setPrevAutoFocus(autoFocus)
    if (autoFocus) setOpen(true)
  }

  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [prevQuery, setPrevQuery] = useState(query)
  const [prevWorkspaces, setPrevWorkspaces] = useState(workspaces)
  if (prevQuery !== query || prevWorkspaces !== workspaces) {
    setPrevQuery(query)
    setPrevWorkspaces(workspaces)
    setActiveIndex(0)
  }
  const [recentIds, setRecentIds] = useState<string[]>(() => safeReadIds(RECENT_KEY))
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => safeReadIds(PINNED_KEY))
  const searchRef = useRef<HTMLInputElement | null>(null)
  const selected = rowById(workspaces, value)

  const filtered = useMemo(() => workspaces
    .map((row) => ({ row, score: rankWorkspace(row, query) }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score || left.row.name.localeCompare(right.row.name))
    .map((entry) => entry.row), [query, workspaces])

  const pinned = pinnedIds.map((id) => rowById(filtered, id)).filter((row): row is OwnerWorkspaceRow => Boolean(row))
  const recent = recentIds
    .filter((id) => !pinnedIds.includes(id))
    .map((id) => rowById(filtered, id))
    .filter((row): row is OwnerWorkspaceRow => Boolean(row))
  const seen = new Set([...pinned, ...recent].map((row) => row.id))
  const all = filtered.filter((row) => !seen.has(row.id))
  const orderedRows = [...pinned, ...recent, ...all]

  useEffect(() => {
    if (!open) return
    const frame = window.requestAnimationFrame(() => {
      searchRef.current?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [open])

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (disabled || event.key !== '/' || open) return
      const target = event.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      event.preventDefault()
      setOpen(true)
    }
    document.addEventListener('keydown', handleShortcut)
    return () => document.removeEventListener('keydown', handleShortcut)
  }, [disabled, open])

  const select = (id: string | null) => {
    onChange(id)
    if (id) {
      const nextRecent = [id, ...recentIds.filter((item) => item !== id)].slice(0, 5)
      setRecentIds(nextRecent)
      safeWriteIds(RECENT_KEY, nextRecent)
    }
    setOpen(false)
    setQuery('')
  }

  const togglePinned = (id: string) => {
    const next = pinnedIds.includes(id) ? pinnedIds.filter((item) => item !== id) : [id, ...pinnedIds.filter((item) => item !== id)].slice(0, 12)
    setPinnedIds(next)
    safeWriteIds(PINNED_KEY, next)
  }

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === '/' && !open) {
      event.preventDefault()
      setOpen(true)
      return
    }
    if (!open) return
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((current) => Math.min(orderedRows.length - 1, current + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => Math.max(0, current - 1))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const row = orderedRows[activeIndex]
      if (row) select(row.id)
    }
  }

  const renderRows = (label: string, rows: OwnerWorkspaceRow[]) => rows.length > 0 ? (
    <section className="ownerWorkspaceSelector__section" aria-label={label}>
      <span>{label}</span>
      {rows.map((row) => {
        const index = orderedRows.findIndex((candidate) => candidate.id === row.id)
        const pinnedRow = pinnedIds.includes(row.id)
        return (
          <div className={`ownerWorkspaceSelector__row${index === activeIndex ? ' isActive' : ''}`} key={row.id} role="option" aria-selected={row.id === value}>
            <button type="button" className="ownerWorkspaceSelector__pick" onClick={() => select(row.id)}>
              <span>
                <strong>{row.name}</strong>
                <small><b>{row.plan}</b><b>{row.users + row.helpers} mbrs</b><b>{row.owner}</b></small>
                <code>{row.id}</code>
              </span>
            </button>
            <button type="button" className="ownerWorkspaceSelector__pin" aria-label={`${pinnedRow ? 'Unpin' : 'Pin'} ${row.name}`} onClick={() => togglePinned(row.id)}>
              {pinnedRow ? <PushPinRoundedIcon fontSize="inherit" /> : <PushPinOutlinedIcon fontSize="inherit" />}
            </button>
          </div>
        )
      })}
    </section>
  ) : null

  return (
    <div className={`ownerWorkspaceSelector ownerWorkspaceSelector--${variant}`} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="ownerWorkspaceSelector__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span>
          <strong>{selected?.name ?? placeholder}</strong>
          <small>{selected ? selected.id : 'Name or workspace id'}</small>
        </span>
        <KeyboardArrowDownRoundedIcon fontSize="inherit" />
      </button>
      {value ? (
        <button type="button" className="ownerWorkspaceSelector__clear" aria-label="Clear workspace selection" disabled={disabled} onClick={() => select(null)}>
          <CloseRoundedIcon fontSize="inherit" />
        </button>
      ) : null}
      {open ? (
        <div className="ownerWorkspaceSelector__popover">
          <label className="ownerWorkspaceSelector__search">
            <SearchRoundedIcon fontSize="inherit" />
            <input ref={searchRef} aria-label="Search workspaces" value={query} onChange={(event) => setQuery(event.target.value)} />
          </label>
          <div className="ownerWorkspaceSelector__list" role="listbox">
            {workspaces.length === 0 ? (
              <p className="ownerWorkspaceSelector__empty">No workspaces loaded - refresh the Controls tab.</p>
            ) : orderedRows.length === 0 ? (
              <p className="ownerWorkspaceSelector__empty">No matching workspaces.</p>
            ) : (
              <>
                {renderRows('Pinned', pinned)}
                {renderRows('Recent', recent)}
                {renderRows('All', all)}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
